import { defineStore } from 'pinia'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import type { ApplicantsState, Applicant } from '@/types/applicants'
import type { Router } from 'vue-router'

const PAGE_SIZE = 20

export const useApplicantsStore = defineStore('applicants', {
  state: (): ApplicantsState => ({
    applicants: [],
    loading: false,
    error: null,
    pagination: {
      totalPages: 1,
      currentPage: 1
    },
    filters: {
      batch: 'all',
      search: '',
      ordering: 'first_name',
      outtalent_program_status: 'all'
    },
    totalCount: 0,
    latestUpdatedApplicants: [],
    latestUpdatedTotalCount: 0
  }),

  getters: {
    hasApplicants: (state) => state.applicants.length > 0,
    isPaginated: (state) => state.pagination.totalPages > 1
  },

  actions: {
    async fetchApplicants({ page = 1 } = {}) {
      this.loading = true
      this.error = null
      try {
        const params: Record<string, any> = { page, page_size: PAGE_SIZE }
        
        // Add batch filter
        if (this.filters.batch !== 'all') {
          params.batch = this.filters.batch
        }
        
        // Add search if present
        if (this.filters.search) {
          params.search = this.filters.search
        }

        // Add ordering if present (skip 'first_name' as it's the default)
        if (this.filters.ordering && this.filters.ordering !== 'first_name') {
          params.ordering = this.filters.ordering
        }

        // Add program status filter
        if (this.filters.outtalent_program_status !== 'all') {
          params.outtalent_program_status = this.filters.outtalent_program_status
        }

        const response = await api.get<{
          count: number
          next: string | null
          previous: string | null
          results: Applicant[]
        }>(API_ROUTES.GET_APPLICANTS_LIST, { params })
        
        this.applicants = response.data.results
        this.totalCount = response.data.count
        this.pagination = {
          totalPages: Math.ceil(response.data.count / PAGE_SIZE),
          currentPage: page
        }
      } catch (error) {
        console.error('Error fetching applicants:', error)
        this.error = 'Failed to fetch applicants'
        this.applicants = []
        this.totalCount = 0
        this.pagination.totalPages = 1
      } finally {
        this.loading = false
      }
    },

    updateRoute(router: Router, updates: Record<string, string | undefined>) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      router.push({
        query: {
          ...router.currentRoute.value.query,
          ...updates
        }
      })
    },

    async setBatch(router: Router, batch: string) {
      this.filters.batch = batch
      this.updateRoute(router, { 
        batch: batch === 'all' ? undefined : batch,
        page: '1'
      })
      await this.fetchApplicants({ page: 1 })
    },

    async setSearch(router: Router, search: string) {
      this.filters.search = search
      this.updateRoute(router, { 
        search: search || undefined,
        page: '1'
      })
      await this.fetchApplicants({ page: 1 })
    },

    async setPage(router: Router, page: number) {
      this.pagination.currentPage = page
      this.updateRoute(router, { page: page.toString() })
      await this.fetchApplicants({ page })
    },

    async setOrdering(router: Router, ordering: string) {
      this.filters.ordering = ordering
      this.updateRoute(router, { 
        ordering: ordering === 'first_name' ? undefined : ordering,
        page: '1'
      })
      await this.fetchApplicants({ page: 1 })
    },

    async setProgramStatus(router: Router, status: string) {
      this.filters.outtalent_program_status = status
      this.updateRoute(router, { 
        outtalent_program_status: status === 'all' ? undefined : status,
        page: '1'
      })
      await this.fetchApplicants({ page: 1 })
    },

    async fetchLatestUpdatedApplicants(timeframe?: number, page_size: number = 50, page: number = 1, ordering: string = '-activity_date') {
      this.loading = true
      this.error = null
  
      try {
        const response = await api.get(API_ROUTES.GET_LATEST_UPDATED_APPLICANTS, {
          params: {
            ...(timeframe ? { timeframe } : {}),
            page_size,
            page,
            ordering
          }
        })
        this.latestUpdatedApplicants = response.data.results
        this.latestUpdatedTotalCount = response.data.count || 0
      } catch (err: any) {
        this.error = err.response?.data?.error || 'Failed to fetch latest updated referrals'
        console.error('Error fetching latest updated referrals:', err)
        throw err
      } finally {
        this.loading = false
      }
    },

    // Initialize store from URL params
    initFromUrl(query: Record<string, string>) {
      if (query.page) {
        this.pagination.currentPage = parseInt(query.page, 10)
      }
      if (query.search) {
        this.filters.search = query.search
      }
      if (query.batch) {
        this.filters.batch = query.batch
      }
      if (query.outtalent_program_status) {
        this.filters.outtalent_program_status = query.outtalent_program_status
      }
      // Set ordering from URL, or default to 'first_name'
      this.filters.ordering = query.ordering || 'first_name'
    }
  }
}) 